<script>
export const pageName = 'admin-record-approvals-view';
export const pageRoute = '/admin/user/{userId}/record-attempt/{attemptId}';
export const title = 'View Record Attempt'
export const requireAdmin = true;

import { commands } from '../../js/lib/functions.js';
import { navigate, PageShowError } from '@trullock/page-manager'
import { showToast } from '../../js/utils/toast.js';
import UserView from '../../../functions/domain/users/userView.js';
import { showConfirm } from '../../js/utils/confirm.js';
import { listenToProjection } from '@trullock/firebase-client';
import { rsoLevelToLevel, recordTypeToEnglish, motorsToString } from '../../../functions/domain/certification-utils.js';
import { getPendingRecordAttempt, getRocketView } from '../../js/readModel.js';

export default {
	data() {
		return { 
			user: null,
			rawAttempt: null,
			rocket: null,
			flight: null,
			reason: '',
			attempt: null,
			ready: false
		}
	},

	methods: {
		rsoLevelToLevel,
		recordTypeToEnglish,
		motorsToString,
		async boot(opts) {
			
			await listenToProjection([UserView, opts.userId], async user => {
				this.user = user;
			});

			this.attempt = await getPendingRecordAttempt(opts.attemptId);
			this.rocket = await getRocketView(opts.userId, this.attempt.rocketId);
			this.flight = this.rocket.getFlight(this.attempt.flightId);

			this.ready = true;
		},
		async show(opts)
		{
			if(this.attempt.result != null)
				throw new PageShowError('/admin', 'Attempt has already been processed', null, 'replace')
		},
		async approve()
		{
			commands.warmUp();

			showConfirm({
				title: 'Confirm approval',
				message: 'Are you sure you want to approve this attempt?' + (this.reason?.length ? '<br><br>The user will be given your message: "' + this.reason + '"' : ''),
				buttons: [
					{
						text: 'Cancel',
						style: 'white'
					},
					{
						text: 'Approve',
						style: 'success',
						action: async () => {
							let result = await commands.users.records.attemptApprove({
								attemptId: this.attempt.id,
								reason: this.reason
							});

							if(!result.success)
							{
								showToast({
									message: 'Error: ' + result.error,
									style: 'bg-danger'
								});

								return;
							}

							navigate('/admin/')

							showToast({
								message: 'Record attempt approved',
								style: 'bg-success'
							})
						}
					}
				]
			});
			
		},
		async reject()
		{
			commands.warmUp();

			if(this.reason?.length == 0)
			{
				this.$refs.reason.setCustomValidity('Required')
				this.$refs.reason.reportValidity();
				return;
			}	
			else
				this.$refs.reason.setCustomValidity('')
			
			showConfirm({
				title: 'Confirm rejection',
				message: 'Are you sure you want to reject this attempt?<br><br>The user will be given your message: "' + this.reason + '"',
				buttons: [
					{
						text: 'Cancel',
						style: 'white'
					},
					{
						text: 'Reject',
						style: 'danger',
						action: async () => {
							let result = await commands.users.records.attemptReject({
								attemptId: this.attempt.id,
								reason: this.reason
							});

							if(!result.success)
							{		
								showToast({
									message: 'Error: ' + result.error,
									style: 'bg-danger'
								});

								return;
							}
							
							navigate('/admin')

							showToast({
								message: 'Record attempt rejected',
								style: 'bg-success'
							})
						}
					}
				]
			});
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row">
			<div class="col">
				<div class="card">
					<div class="card-body">

						<a :href="`/admin/user/${user.id}`" class="avatar avatar-xl card-avatar">
							<img :src="user.avatar" class="avatar-img rounded-circle border border-4 border-card" alt="...">
						</a>

						<h2 class="card-title text-center">
							<a :href="`/admin/user/${user.id}`">{{ user.name }}</a>
						</h2>

						<p class="text-center mb-4">
							<span :class="`badge mx-2 ${user.certification.level1.status == 'passed' ? 'badge-success' : (user.certification.level1.status == 'in-progress' ? 'badge-secondary' : 'badge-light') }`">Level 1</span>
							<span :class="`badge mx-2 ${user.certification.level2.status == 'passed' ? 'badge-success' : (user.certification.level2.status == 'in-progress' ? 'badge-secondary' : 'badge-light')}`">Level 2</span>
							<span :class="`badge mx-2 ${user.certification.level3.status == 'passed' ? 'badge-success' : (user.certification.level3.status == 'in-progress' ? 'badge-secondary' : 'badge-light')}`">Level 3</span>
							<span v-if="user.rso.level" class="badge mx-2 badge-success">{{ rsoLevelToLevel(user.rso.level) }} RSO</span>
							<span v-if="user.map.isAssessor" class="badge mx-2 badge-success">MAP Assessor</span>
						</p>
					</div>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col col-lg-4">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Attempt details</h4>
					</div>
					<div class="card-body">
						<div class="list-group list-group-flush mb-4">
							
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Team name
									</div>
									<div class="col-auto">
										{{ flight.teamName || 'N/A' }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										<span>Email</span>
									</div>
									<div class="col-auto">
										<span class="text-body-secondary">
											<span class="selectable">{{ user.email }}</span>
										</span>
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										<small>D.o.B</small>
									</div>
									<div class="col-auto">
										<small class="text-body-secondary">
											{{ user.bornOn?.format('yyyy/MM/dd') }} ({{ user.age }}yrs)
										</small>
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Flight date
									</div>
									<div class="col-auto">
										{{  flight.flownOn.format('yyyy/MM/dd') }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Flight location
									</div>
									<div class="col-auto">
										{{  flight.location }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Rocket Name
									</div>
									<div class="col-auto">
										{{  rocket.name }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Motor(s)
									</div>
									<div class="col-auto">
										{{ motorsToString(flight.motors) }} - {{ flight.ejections }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Build Type
									</div>
									<div class="col-auto">
										{{  rocket.buildType }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Notes
									</div>
									<div class="col-auto">
										{{  flight.notes }}
									</div>
								</div>
							</div>
							
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Record type
									</div>
									<div class="col-auto">
										{{ recordTypeToEnglish(flight.recordType) }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Record class
									</div>
									<div class="col-auto">
										{{ flight.recordClass }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Altitude reached
									</div>
									<div class="col-auto">
										{{ flight.reachedAlt }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Altimeter
									</div>
									<div class="col-auto">
										{{ flight.altimeter }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Witness
									</div>
									<div class="col-auto">
										{{ flight.witness }}
									</div>
								</div>
							</div>
							<div v-for="(log, index) in flight.logs" class="list-group-item">
								<div class="row align-items-center small">
									<div class="col ms-n2">
										{{ log.name }}
									</div>
									<div class="col-auto">
										<a :href="`/rockets/${rocket.id}/flights/${flight.id}/logs/${log.id}?userId=${user.id}`" class="btn btn-sm btn-outline-primary" type="button"><span class="fe fe-eye"></span> View</a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="col col-lg-8">
				<div v-if="rocket.photoUrl" class="card">
					<div class="card-body img-container">
						<a :href="rocket.photoUrl" target="_blank"><img :src="rocket.photoUrl" /></a>
					</div>
				</div>
				<div class="card">
					<div class="card-body img-container">
						<a :href="flight.flightCardUrl" target="_blank"><img :src="flight.flightCardUrl" /></a>
					</div>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col">
				<form>
					<div class="form-group mb-4">
						<textarea v-model="reason" required ref="reason" placeholder="Notes for the member" rows="5" class="form-control"></textarea>
						<span class="invalid-feedback"></span>
					</div>
					<div class="row">
						<div class="col"></div>
						<div class="col-auto">
							<button type="submit" @click.prevent="approve" class="btn btn-success">Approve <span class="fe fe-check-circle"></span></button>
						</div>
						<div class="col-auto">
							<button type="submit" @click.prevent="reject" class="btn btn-danger">Reject <span class="fe fe-x-circle"></span></button>
						</div>
					</div>
				</form>
			</div>
		</div>
    </div>
</template>
